import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Paper,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import PainelAprovacao from "../components/Aprovacao/PainelAprovacao";
import DialogReprovarAprovacao from "../components/Aprovacao/DialogReprovarAprovacao";
import DialogQuestionarAprovacao from "../components/Aprovacao/DialogQuestionarAprovacao";
import DialogResponderAprovacao from "../components/Aprovacao/DialogResponderAprovacao";
import { FaEdit, FaPaperclip, FaSave, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import mammoth from "mammoth";
import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";
import { TAGS_TEMPLATE_CONTRATO, TAGS_CONDICIONAIS_TEMPLATE_CONTRATO, TAGS_BLOCOS_TABELA_TEMPLATE_CONTRATO } from "../data/tagsTemplateContrato";

const formVazio = { nome: "", arquivo: null, nomeArquivoOriginal: "", nomeArquivoPersonalizado: "" };

// Troca cada [Tag] pelo valor de exemplo correspondente, pra pré-visualização.
function substituirTagsPorExemplo(html) {
  let resultado = html;
  TAGS_TEMPLATE_CONTRATO.forEach(({ tag, exemplo }) => {
    resultado = resultado.split(`[${tag}]`).join(exemplo);
  });
  return resultado;
}

// Casa cada marcador (abertura OU fechamento) isolado, não o par — a simulação
// suporta aninhamento (ex: [SE:FiadorCasado] com [SE:Xxxx] dentro), mesma lógica
// de pilha usada na geração real (ver aplicarCondicionais em gerarContratoDeTemplate.js).
const REGEX_MARCADOR_CONDICIONAL = /\[(\/?)SE:(\w+)\]/g;

// Mantém ou remove cada bloco [SE:Condicao]...[/SE:Condicao] conforme condicoesAtivas.
// Um fechamento só casa com o marcador de abertura mais recente ainda aberto (pilha);
// se um nível estiver inativo, tudo dentro dele some, mesmo com níveis aninhados ativos.
function aplicarCondicionais(html, condicoesAtivas) {
  const marcadores = [...html.matchAll(REGEX_MARCADOR_CONDICIONAL)];
  const pilha = [];
  const remover = []; // pares [inicio, fim) a cortar do html

  for (const m of marcadores) {
    const nome = m[2];
    const inicio = m.index;
    const fim = m.index + m[0].length;
    if (m[1] !== "/") {
      pilha.push({ nome, inicio, fim });
    } else if (pilha.length > 0 && pilha[pilha.length - 1].nome === nome) {
      const aberto = pilha.pop();
      remover.push([aberto.inicio, aberto.fim]); // marcador de abertura, sempre some
      remover.push([inicio, fim]); // marcador de fechamento, sempre some
      if (!condicoesAtivas[nome]) remover.push([aberto.fim, inicio]); // conteúdo, só se inativo
    }
    // fechamento fora de ordem/órfão: ignora, sobra como está no html (mesma rede de
    // segurança da geração real — fica visível em vez de sumir escondido).
  }

  remover.sort((a, b) => a[0] - b[0]);
  let resultado = "";
  let cursor = 0;
  for (const [inicio, fim] of remover) {
    if (inicio < cursor) continue; // já coberto por um corte de nível mais externo
    resultado += html.slice(cursor, inicio);
    cursor = Math.max(cursor, fim);
  }
  resultado += html.slice(cursor);
  return resultado;
}

// Descobre quais condições [SE:Nome] o próprio .docx utiliza, pra só oferecer
// os toggles relevantes (em vez de listar todas as condições cadastradas no dicionário).
function detectarCondicoes(html) {
  const nomes = new Set();
  for (const match of html.matchAll(REGEX_MARCADOR_CONDICIONAL)) nomes.add(match[2]);
  return Array.from(nomes);
}

const REGEX_BLOCO_TABELA = /\[BLOCO:(\w+)\][\s\S]*?\[\/BLOCO:\1\]/g;

// Linhas { label, value, keepCase } vindas de GET /api/propostas/quadro-resumo/exemplo —
// mesma fonte/regras da impressão da Proposta e da geração do Contrato (ver
// quadroResumo.service.js no backend). Só para fins de pré-visualização.
function tabelaExemploHtml(linhas) {
  const linhasHtml = (linhas || [])
    .map(({ label, value }, idx) => {
      const bg = idx % 2 === 1 ? "#f3e8e6" : "#fff";
      return `<tr><td style="width:42%;font-style:italic;background-color:${bg}">${label}</td><td style="background-color:${bg}">${value}</td></tr>`;
    })
    .join("");
  return `<table><thead><tr><th colspan="2" style="background-color:#d9d9d9;text-align:center">QUADRO RESUMO</th></tr></thead><tbody>${linhasHtml}</tbody></table>`;
}

const GERADORES_EXEMPLO_BLOCO_TABELA = {
  QuadroResumo: (linhasExemploQuadroResumo) => tabelaExemploHtml(linhasExemploQuadroResumo),
};

// Troca cada [BLOCO:Nome]...[/BLOCO:Nome] por uma tabela HTML de exemplo, pra
// pré-visualização — o bloco em si não é condicional, sempre vira tabela.
function aplicarBlocosDeTabela(html, linhasExemploQuadroResumo) {
  return html.replace(REGEX_BLOCO_TABELA, (match, nome) => {
    const gerador = GERADORES_EXEMPLO_BLOCO_TABELA[nome];
    return gerador ? gerador(linhasExemploQuadroResumo) : match;
  });
}

function formatDataHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}
const STATUS_APROVACAO_TEMPLATE_CONFIG = {
  EmElaboracao: {
    label: "Em Elaboração",
    cor: "#555",
  },
  EmAprovacao: {
    label: "Em Aprovação",
    cor: "#1998a8",
  },
  Aprovado: {
    label: "Aprovado",
    cor: "#2e7d32",
  },
  Reprovado: {
    label: "Reprovado",
    cor: "#d32f2f",
  },
  Inutilizado: {
    label: "Inutilizado",
    cor: "#795548",
  },
};

function chipStatusAprovacaoTemplate(status) {
  const config = STATUS_APROVACAO_TEMPLATE_CONFIG[status] ?? STATUS_APROVACAO_TEMPLATE_CONFIG.EmElaboracao;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.35,
        borderRadius: 1,
        backgroundColor: config.cor,
        color: "#fff",
        fontSize: "0.75rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {config.label}
    </Box>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const nomeInputRef = useRef(null);

  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });

  const [abaAtiva, setAbaAtiva] = useState("dados");

  const [aprovacaoInfo, setAprovacaoInfo] = useState({
    aprovacao: null,
    autorizacoes: [],
    historicos: [],
    usuarioEhAprovador: false,
    usuarioEmAlcada: false,
  });

  const [enviandoAprovacao, setEnviandoAprovacao] = useState(false);
  const [processandoAprovacao, setProcessandoAprovacao] = useState(false);

  const [usuariosAprovadores, setUsuariosAprovadores] = useState([]);

  const [dialogReprovar, setDialogReprovar] = useState({
    aberto: false,
    motivo: "",
  });

  const [dialogQuestionar, setDialogQuestionar] = useState({
    aberto: false,
    motivo: "",
    idsUsuarioResposta: [],
    arquivo: null,
  });

  const [dialogResponder, setDialogResponder] = useState({
    aberto: false,
    resposta: "",
    pergunta: null,
    arquivo: null,
  });

  const [confirmRevisar, setConfirmRevisar] = useState({
    aberto: false,
    motivo: "",
  });

  const [confirmEditarAprovado, setConfirmEditarAprovado] = useState(false);

  const [confirmInutilizar, setConfirmInutilizar] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });
  const [confirmExcluirArquivo, setConfirmExcluirArquivo] = useState(false);
  const [errosForm, setErrosForm] = useState({});

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  const [tagsDialogAberto, setTagsDialogAberto] = useState(false);
  const [tagsBusca, setTagsBusca] = useState("");
  const [tagsDialogAba, setTagsDialogAba] = useState(0);

  const [previewAberto, setPreviewAberto] = useState(false);
  const [previewCarregando, setPreviewCarregando] = useState(false);
  const [previewHtmlBruto, setPreviewHtmlBruto] = useState("");
  const [previewSemSubstituicao, setPreviewSemSubstituicao] = useState(false);
  const [condicoesDetectadas, setCondicoesDetectadas] = useState([]);
  const [condicoesAtivas, setCondicoesAtivas] = useState({});
  const [linhasExemploQuadroResumo, setLinhasExemploQuadroResumo] = useState([]);

  const [visualizacaoPdf, setVisualizacaoPdf] = useState({
    aberto: false,
    carregando: false,
    url: "",
    nome: "",
  });

  // Busca uma vez só — dado estático de exemplo, mesma fonte usada na impressão
  // real da Proposta e na geração do Contrato (ver quadroResumo.service.js).
  useEffect(() => {
    const tk = localStorage.getItem("token");
    fetch("/api/propostas/quadro-resumo/exemplo", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : { linhas: [] }))
      .then(({ linhas }) => setLinhasExemploQuadroResumo(linhas))
      .catch(() => setLinhasExemploQuadroResumo([]));
  }, []);
  useEffect(() => {
    const tk = localStorage.getItem("token");

    fetch("/api/propostas/usuarios-aprovadores", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((dados) => {
        setUsuariosAprovadores(Array.isArray(dados) ? dados : []);
      })
      .catch(() => {
        setUsuariosAprovadores([]);
      });
  }, []);
  const exibirPreview = (arquivo, { semSubstituicao = false } = {}) => {
    setPreviewAberto(true);
    setPreviewCarregando(true);
    setPreviewHtmlBruto("");
    setPreviewSemSubstituicao(semSubstituicao);
    setCondicoesDetectadas([]);
    setCondicoesAtivas({});
    arquivo
      .arrayBuffer()
      .then((buffer) => mammoth.convertToHtml({ arrayBuffer: buffer }))
      .then((resultado) => {
        const condicoes = detectarCondicoes(resultado.value);
        setPreviewHtmlBruto(resultado.value);
        setCondicoesDetectadas(condicoes);
        setCondicoesAtivas(Object.fromEntries(condicoes.map((nome) => [nome, true])));
      })
      .catch(() => {
        setPreviewAberto(false);
        mostrarSnack("Não foi possível gerar a pré-visualização do template.", "error");
      })
      .finally(() => setPreviewCarregando(false));
  };

  const handleToggleCondicao = (nome) => {
    setCondicoesAtivas((prev) => ({ ...prev, [nome]: !prev[nome] }));
  };

  // Reaplica condicionais + substituição de tags a cada toggle, sem reconverter o docx —
  // exceto quando é só a visualização do anexo bruto, que mostra o documento como está.
  const previewHtml = useMemo(
    () =>
      previewSemSubstituicao
        ? previewHtmlBruto
        : substituirTagsPorExemplo(aplicarBlocosDeTabela(aplicarCondicionais(previewHtmlBruto, condicoesAtivas), linhasExemploQuadroResumo)),
    [previewHtmlBruto, condicoesAtivas, previewSemSubstituicao, linhasExemploQuadroResumo],
  );

  // ── Permissões ────────────────────────────────────────────────────────────
  const token = localStorage.getItem("token");

  let tipoUsuario = "";
  let userId = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);

      userId = Number(decoded.id ?? decoded.usuarioId ?? decoded.userId) || null;

      tipoUsuario = decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // ── Listagem (server-side filtro + paginação) ────────────────────────────
  const carregarTemplates = useCallback(() => {
    const tk = localStorage.getItem("token");
    const params = new URLSearchParams({ filtro, pagina, porPagina });
    setCarregando(true);
    fetch(`/api/templates?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setTemplates(Array.isArray(data) ? data : []);
        setTotal(t ?? 0);
      })
      .catch((err) => console.error("Erro ao buscar templates:", err))
      .finally(() => setCarregando(false));
  }, [filtro, pagina, porPagina]);

  useEffect(() => {
    if (!podeVisualizar) return;
    carregarTemplates();
  }, [podeVisualizar, carregarTemplates]);

  // ── Template — aprovação ────────────────────────────────────────────────────
  const carregarAprovacao = useCallback((id) => {
    const tk = localStorage.getItem("token");

    return fetch(`/api/templates/${id}`, {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return null;

        setTemplates((prev) => prev.map((template) => (template.id === id ? { ...template, ...data } : template)));

        setAprovacaoInfo({
          aprovacao: (data.aprovacoes ?? []).find((a) => a.processoTipo === "TemplateContrato") ?? null,

          autorizacoes: (data.autorizacoes ?? []).filter((a) => a.processoTipo === "TemplateContrato"),

          historicos: (data.historicos ?? []).filter((h) => h.processoTipo === "TemplateContrato"),

          usuarioEhAprovador: data.usuarioEhAprovador ?? false,
          usuarioEmAlcada: data.usuarioEmAlcada ?? false,
        });

        return data;
      })
      .catch((erro) => {
        console.error("Erro ao carregar aprovação do template:", erro);
        return null;
      });
  }, []);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  // =======================
  // COPIAR
  // =======================
  const handleCopiar = async () => {
    const texto = templates
      .map((template) => {
        const enviadoEm = formatDataHora(template.dataUpload);
        const arquivo = template.nomeArquivoOriginal || "";
        const minuta = template.nome || "";

        const status = STATUS_APROVACAO_TEMPLATE_CONFIG[template.statusAprovacao]?.label || "Em Elaboração";

        return [enviadoEm, arquivo, minuta, status].join(" - ");
      })
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Enviado em", "Arquivo", "Minuta", "Status"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = templates.map((template) =>
      [
        formatDataHora(template.dataUpload),
        template.nomeArquivoOriginal || "",
        template.nome || "",
        STATUS_APROVACAO_TEMPLATE_CONFIG[template.statusAprovacao]?.label || "Em Elaboração",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "templates-de-contrato.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  // ── Form inferior ─────────────────────────────────────────────────────────
  const handleVisualizar = (template) => {
    setEditandoId(template.id);
    setSomenteLeitura(true);
    setAbaAtiva("dados");

    setAprovacaoInfo({
      aprovacao: null,
      autorizacoes: [],
      historicos: [],
      usuarioEhAprovador: false,
      usuarioEmAlcada: false,
    });

    setForm({
      nome: template.nome,
      arquivo: null,
      nomeArquivoOriginal: template.nomeArquivoOriginal,
      nomeArquivoPersonalizado: template.nomeArquivoOriginal,
    });

    setGridMinimizado(true);

    carregarAprovacao(template.id);

    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });

    setTimeout(() => nomeInputRef.current?.focus(), 300);
  };
  const chamarAprovacao = async (url, body, setProcessando, msgErro) => {
    const tk = localStorage.getItem("token");

    setProcessando(true);

    try {
      const ehFormData = body instanceof FormData;

      const resposta = await fetch(url, {
        method: "POST",
        headers: ehFormData
          ? {
              Authorization: `Bearer ${tk}`,
            }
          : {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tk}`,
            },
        body: ehFormData ? body : JSON.stringify(body),
      });

      const dados = await resposta.json().catch(() => ({}));

      if (!resposta.ok) {
        throw new Error(dados.erro || msgErro);
      }

      if (editandoId) {
        await carregarAprovacao(editandoId);
      }

      carregarTemplates();

      return dados;
    } catch (erro) {
      mostrarSnack(erro.message || msgErro, "error");
      return null;
    } finally {
      setProcessando(false);
    }
  };
  const handleEnviarAprovacao = () => {
    if (!editandoId) return;

    chamarAprovacao(`/api/templates/${editandoId}/enviar-aprovacao`, {}, setEnviandoAprovacao, "Erro ao enviar para aprovação.").then((resultado) => {
      if (resultado) {
        mostrarSnack("Template enviado para aprovação!");
      }
    });
  };

  const handleAprovar = () => {
    if (!editandoId) return;

    chamarAprovacao(`/api/templates/${editandoId}/aprovar`, {}, setProcessandoAprovacao, "Erro ao aprovar.").then((resultado) => {
      if (resultado) {
        mostrarSnack("Template aprovado!");
      }
    });
  };

  const handleReprovar = () => {
    if (!editandoId) return;
    if (!dialogReprovar.motivo.trim()) return;

    chamarAprovacao(
      `/api/templates/${editandoId}/reprovar`,
      {
        motivo: dialogReprovar.motivo,
      },
      setProcessandoAprovacao,
      "Erro ao reprovar.",
    ).then((resultado) => {
      if (!resultado) return;

      setDialogReprovar({
        aberto: false,
        motivo: "",
      });

      mostrarSnack("Template reprovado.", "warning");
    });
  };

  const handleQuestionar = () => {
    if (!editandoId) return;

    if (!dialogQuestionar.motivo.trim() || !dialogQuestionar.idsUsuarioResposta.length) {
      return;
    }

    const fd = new FormData();

    fd.append("motivo", dialogQuestionar.motivo);

    fd.append("idsUsuarioResposta", JSON.stringify(dialogQuestionar.idsUsuarioResposta));

    if (dialogQuestionar.arquivo) {
      fd.append("arquivo", dialogQuestionar.arquivo);
    }

    chamarAprovacao(`/api/templates/${editandoId}/questionar`, fd, setProcessandoAprovacao, "Erro ao questionar.").then((resultado) => {
      if (!resultado) return;

      setDialogQuestionar({
        aberto: false,
        motivo: "",
        idsUsuarioResposta: [],
        arquivo: null,
      });

      mostrarSnack("Questionamento enviado.");
    });
  };

  const handleResponder = () => {
    if (!editandoId) return;
    if (!dialogResponder.resposta.trim()) return;

    const fd = new FormData();

    fd.append("resposta", dialogResponder.resposta);

    if (dialogResponder.pergunta?.id) {
      fd.append("historicoPerguntaId", String(dialogResponder.pergunta.id));
    }

    if (dialogResponder.arquivo) {
      fd.append("arquivo", dialogResponder.arquivo);
    }

    chamarAprovacao(`/api/templates/${editandoId}/responder`, fd, setProcessandoAprovacao, "Erro ao responder.").then((resultado) => {
      if (!resultado) return;

      setDialogResponder({
        aberto: false,
        resposta: "",
        pergunta: null,
        arquivo: null,
      });

      mostrarSnack("Resposta enviada.");
    });
  };

  const handleRevisar = () => {
    if (!editandoId) return;
    if (!confirmRevisar.motivo.trim()) return;

    chamarAprovacao(
      `/api/templates/${editandoId}/revisar`,
      {
        motivo: confirmRevisar.motivo,
      },
      setProcessandoAprovacao,
      "Erro ao solicitar revisão.",
    ).then((resultado) => {
      if (!resultado) return;

      setConfirmRevisar({
        aberto: false,
        motivo: "",
      });

      mostrarSnack("Template devolvido para elaboração.");
    });
  };

  const handleHabilitarEdicao = () => {
    const templateAtual = templates.find((template) => template.id === editandoId);

    const status = templateAtual?.statusAprovacao || "EmElaboracao";

    if (status === "EmAprovacao") {
      mostrarSnack("Template em aprovação não pode ser editado.", "warning");
      return;
    }

    if (status === "Aprovado") {
      setConfirmEditarAprovado(true);
      return;
    }

    if (status === "Reprovado") {
      mostrarSnack("Template reprovado deve passar por Revisão antes de ser editado.", "warning");
      return;
    }

    if (status === "Inutilizado") {
      mostrarSnack("Template inutilizado não pode ser editado.", "warning");
      return;
    }

    setSomenteLeitura(false);

    setTimeout(() => {
      nomeInputRef.current?.focus();
    }, 50);
  };
  const handleConfirmarEdicaoAprovado = () => {
    if (!editandoId) return;

    chamarAprovacao(`/api/templates/${editandoId}/reabrir-edicao`, {}, setProcessandoAprovacao, "Erro ao reabrir template para edição.").then((resultado) => {
      if (!resultado) return;

      setConfirmEditarAprovado(false);

      setSomenteLeitura(false);

      mostrarSnack("Template devolvido para Em Elaboração. Após editar, será necessário enviá-lo novamente para aprovação.", "warning");

      setTimeout(() => {
        nomeInputRef.current?.focus();
      }, 50);
    });
  };
  const handleInutilizar = () => {
    if (!editandoId) return;

    chamarAprovacao(`/api/templates/${editandoId}/inutilizar`, {}, setProcessandoAprovacao, "Erro ao inutilizar template.").then((resultado) => {
      if (!resultado) return;

      setConfirmInutilizar(false);

      mostrarSnack("Template inutilizado.", "warning");
    });
  };

  const handleCancelarForm = () => {
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio });
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSalvar = () => {
    const novosErros = {};
    if (!form.nome.trim()) novosErros.nome = "Nome é obrigatório.";
    if (!form.arquivo && !form.nomeArquivoOriginal) novosErros.arquivo = "Selecione um arquivo .docx.";
    if (Object.keys(novosErros).length > 0) {
      setErrosForm(novosErros);
      nomeInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});

    const tk = localStorage.getItem("token");
    const url = editandoId ? `/api/templates/${editandoId}` : "/api/templates";
    const method = editandoId ? "PUT" : "POST";

    const fd = new FormData();
    fd.append("nome", form.nome);
    if (form.arquivo) fd.append("arquivo", form.arquivo);
    if (form.nomeArquivoPersonalizado.trim()) fd.append("nomeArquivo", form.nomeArquivoPersonalizado.trim());
    const arquivoEnviado = form.arquivo;

    fetch(url, { method, headers: { Authorization: `Bearer ${tk}` }, body: fd })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack(editandoId ? "Template atualizado!" : "Template cadastrado com sucesso!");
        handleCancelarForm();
        carregarTemplates();
        if (arquivoEnviado) exibirPreview(arquivoEnviado);
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  const handleConfirmarDelete = (id) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id });
  };

  const handleExcluir = () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    const tk = localStorage.getItem("token");
    fetch(`/api/templates/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
      .then((res) => {
        if (!res.ok && res.status !== 204) {
          mostrarSnack("Erro ao excluir.", "error");
          return;
        }
        if (editandoId === id) handleCancelarForm();
        mostrarSnack("Template excluído.", "warning");
        carregarTemplates();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  const handleConfirmarExcluirArquivo = () => {
    document.activeElement?.blur();
    setConfirmExcluirArquivo(true);
  };

  // ── Descarta o arquivo já salvo, dentro do form de edição (só em memória — o
  // arquivo é obrigatório no Template, então isso não é persistido sozinho: pra
  // salvar, obrigatoriamente é preciso escolher outro arquivo antes de "Atualizar").
  const handleExcluirArquivo = () => {
    setConfirmExcluirArquivo(false);
    setForm((p) => ({ ...p, arquivo: null, nomeArquivoOriginal: "", nomeArquivoPersonalizado: "" }));
  };

  const handleVisualizarArquivoSalvo = async (id, nomeOriginal) => {
    const tk = localStorage.getItem("token");

    setVisualizacaoPdf({
      aberto: true,
      carregando: true,
      url: "",
      nome: nomeOriginal || "Template",
    });

    try {
      const resposta = await fetch(`/api/templates/${id}/visualizar-pdf`, {
        headers: {
          Authorization: `Bearer ${tk}`,
        },
      });

      if (!resposta.ok) {
        const dados = await resposta.json().catch(() => ({}));
        throw new Error(dados.erro || "Erro ao visualizar o template.");
      }

      const blob = await resposta.blob();

      const pdfBlob = new Blob([blob], {
        type: "application/pdf",
      });

      const url = URL.createObjectURL(pdfBlob);

      setVisualizacaoPdf({
        aberto: true,
        carregando: false,
        url,
        nome: nomeOriginal || "Template",
      });
    } catch (erro) {
      setVisualizacaoPdf({
        aberto: false,
        carregando: false,
        url: "",
        nome: "",
      });

      mostrarSnack(erro.message || "Erro ao visualizar o template.", "error");
    }
  };

  const fecharVisualizacaoPdf = () => {
    setVisualizacaoPdf((anterior) => {
      if (anterior.url) {
        URL.revokeObjectURL(anterior.url);
      }

      return {
        aberto: false,
        carregando: false,
        url: "",
        nome: "",
      };
    });
  };

  const handleDownload = (id, nomeOriginal) => {
    const tk = localStorage.getItem("token");
    fetch(`/api/templates/${id}/download`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject("Erro ao baixar.")))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nomeOriginal;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => mostrarSnack("Erro ao baixar arquivo.", "error"));
  };

  // ── Dialog de tags — filtro client-side ──────────────────────────────────
  const tagsFiltradas = TAGS_TEMPLATE_CONTRATO.filter((t) => {
    const alvo = tagsBusca.trim().toLowerCase();
    if (!alvo) return true;
    return t.tag.toLowerCase().includes(alvo) || t.descricao.toLowerCase().includes(alvo);
  });

  const condicionaisFiltradas = TAGS_CONDICIONAIS_TEMPLATE_CONTRATO.filter((c) => {
    const alvo = tagsBusca.trim().toLowerCase();
    if (!alvo) return true;
    return c.condicao.toLowerCase().includes(alvo) || c.descricao.toLowerCase().includes(alvo);
  });

  const blocosFiltrados = TAGS_BLOCOS_TABELA_TEMPLATE_CONTRATO.filter((b) => {
    const alvo = tagsBusca.trim().toLowerCase();
    if (!alvo) return true;
    return b.bloco.toLowerCase().includes(alvo) || b.descricao.toLowerCase().includes(alvo);
  });

  let categoriaAnterior = null;
  let categoriaCondicionalAnterior = null;

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título + botão de tags */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Templates de Contrato
          </Typography>
          <Button
            startIcon={<ListAltIcon />}
            onClick={() => setTagsDialogAberto(true)}
            sx={{
              width: "auto",
              backgroundColor: "#1998a8",
              color: "#fff",
              "&:hover": { backgroundColor: "#1565c0" },
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Ver Tags Disponíveis
          </Button>
        </Box>

        {/* Filtro + Copiar + CSV */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Template"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                color: "var(--ig-t-ffffff)",
              },
              "& input::placeholder": {
                color: "var(--ig-t-ffffff)",
                opacity: 1,
                fontWeight: 500,
                fontSize: "1rem",
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* Minimizar grid */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* Grid */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            {carregando ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Ações
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Enviado em
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: "50%",
                      }}
                    >
                      Arquivo
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: "50%",
                      }}
                    >
                      Minuta
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                        width: 150,
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                        Nenhum template encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow
                        key={template.id}
                        sx={{
                          "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                          backgroundColor: editandoId === template.id ? "rgba(25,152,168,0.08)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          {podeVisualizar && (
                            <Tooltip title="Visualizar / Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleVisualizar(template)}
                                sx={{
                                  backgroundColor: "#1998a8",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#1565c0" },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {podeVisualizar && (
                            <Tooltip title="Baixar arquivo">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(template.id, template.nomeArquivoOriginal)}
                                sx={{
                                  backgroundColor: "#555",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#444" },
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {podeExcluir && template.statusAprovacao === "EmElaboracao" && (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={() => handleConfirmarDelete(template.id)}
                                sx={{
                                  backgroundColor: "#d32f2f",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#c62828" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                          {formatDataHora(template.dataUpload)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)" }}>{template.nomeArquivoOriginal}</TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>{template.nome}</TableCell>
                        <TableCell
                          sx={{
                            borderBottom: "1px solid var(--ig-b-444444)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chipStatusAprovacaoTemplate(template.statusAprovacao)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {!carregando && (
              <TablePagination
                component="div"
                count={total}
                page={pagina}
                onPageChange={(_, p) => setPagina(p)}
                rowsPerPage={porPagina}
                onRowsPerPageChange={(e) => {
                  setPorPagina(parseInt(e.target.value, 10));
                  setPagina(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Templates por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{
                  color: "var(--ig-t-ffffff)",
                  borderTop: "1px solid var(--ig-b-444444)",
                  "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                  "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                  "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
                }}
              />
            )}
          </TableContainer>
        </Collapse>

        {/* Formulário inferior */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Template" : editandoId ? "Editar Template" : "Novo Template"}
            </Typography>

            {editandoId && (
              <Tabs
                value={abaAtiva}
                onChange={(_, value) => setAbaAtiva(value)}
                sx={{
                  mb: 2,
                  borderBottom: "1px solid var(--ig-b-333333)",
                  "& .MuiTab-root": {
                    color: "var(--ig-t-aaaaaa)",
                    textTransform: "none",
                    fontWeight: 600,
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#1998a8",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1998a8",
                  },
                }}
              >
                <Tab value="dados" label="DADOS" />
                <Tab value="aprovacao" label="APROVAÇÃO" />
              </Tabs>
            )}

            {(!editandoId || abaAtiva === "dados") && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Nome do Template"
                  size="small"
                  fullWidth
                  inputRef={nomeInputRef}
                  value={form.nome}
                  onChange={(e) => {
                    if (!somenteLeitura) {
                      setForm((p) => ({ ...p, nome: e.target.value.toUpperCase() }));
                      if (errosForm.nome) setErrosForm((p) => ({ ...p, nome: undefined }));
                    }
                  }}
                  error={!!errosForm.nome}
                  helperText={errosForm.nome}
                  slotProps={{ input: { readOnly: somenteLeitura } }}
                  sx={{
                    "& .MuiInputLabel-root": { color: errosForm.nome ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
                      color: "var(--ig-t-ffffff)",
                      "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-444444)" },
                      "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-888888)" },
                    },
                    "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" },
                    "& .MuiFormHelperText-root": { color: "#f44336" },
                  }}
                />

                {!somenteLeitura && editandoId && form.nomeArquivoOriginal && (
                  <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleVisualizarArquivoSalvo(editandoId, form.nomeArquivoOriginal)}
                      sx={{
                        backgroundColor: "#1998a8",
                        color: "#fff",
                        fontSize: "0.71rem",
                        minWidth: 0,
                        px: 1.2,
                        width: "auto",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#1565c0" },
                      }}
                    >
                      Visualizar
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleDownload(editandoId, form.nomeArquivoOriginal)}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        fontSize: "0.71rem",
                        minWidth: 0,
                        px: 1.2,
                        width: "auto",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#1565c0" },
                      }}
                    >
                      Baixar
                    </Button>
                    {podeExcluir && (
                      <Button
                        size="small"
                        startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                        onClick={handleConfirmarExcluirArquivo}
                        sx={{
                          backgroundColor: "#d32f2f",
                          color: "#fff",
                          fontSize: "0.71rem",
                          minWidth: 0,
                          px: 1.2,
                          width: "auto",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#c62828" },
                        }}
                      >
                        Excluir
                      </Button>
                    )}
                  </Box>
                )}

                {!somenteLeitura && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        component="label"
                        startIcon={<FaPaperclip />}
                        variant="contained"
                        sx={{
                          width: "auto",
                          backgroundColor: "#007bff",
                          fontWeight: 700,
                          textTransform: "none",
                          px: 1.5,
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": { backgroundColor: "#0069d9" },
                        }}
                        title={form.arquivo?.name || ""}
                      >
                        {form.arquivo?.name || "GED"}
                        <input
                          type="file"
                          accept=".docx"
                          hidden
                          onChange={(e) => {
                            setForm((p) => ({ ...p, arquivo: e.target.files[0], nomeArquivoPersonalizado: "" }));
                            if (errosForm.arquivo) setErrosForm((p) => ({ ...p, arquivo: undefined }));
                          }}
                        />
                      </Button>
                      {(form.arquivo || form.nomeArquivoOriginal) && (
                        <TextField
                          size="small"
                          placeholder="Nome descritivo (opcional)"
                          value={form.nomeArquivoPersonalizado}
                          onChange={(e) => setForm((p) => ({ ...p, nomeArquivoPersonalizado: e.target.value }))}
                          sx={{
                            flex: 1,
                            minWidth: 130,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "var(--ig-f-2a2a2a)",
                              color: "var(--ig-t-ffffff)",
                              "& fieldset": { borderColor: "var(--ig-b-444444)" },
                              "&:hover fieldset": { borderColor: "var(--ig-b-888888)" },
                            },
                            "& input": { color: "var(--ig-t-ffffff)", fontSize: "0.85rem", py: "8px" },
                          }}
                        />
                      )}
                      {form.arquivo && (
                        <Button
                          size="small"
                          onClick={() => setForm((p) => ({ ...p, arquivo: null, nomeArquivoPersonalizado: p.nomeArquivoOriginal }))}
                          sx={{
                            backgroundColor: "#d32f2f",
                            color: "#fff",
                            fontSize: "0.74rem",
                            "&:hover": { backgroundColor: "#c62828" },
                            textTransform: "none",
                            px: 1.5,
                            width: "auto",
                          }}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Box>
                    {errosForm.arquivo && (
                      <Typography variant="caption" sx={{ color: "#f44336", display: "block", mt: 0.5 }}>
                        {errosForm.arquivo}
                      </Typography>
                    )}
                  </Box>
                )}

                {somenteLeitura && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      label="Arquivo"
                      size="small"
                      fullWidth
                      value={form.nomeArquivoOriginal}
                      slotProps={{ input: { readOnly: true } }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "var(--ig-t-ffffff)",
                          fontWeight: "bold",
                        },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-ffffff)",
                          "& fieldset": {
                            borderColor: "var(--ig-b-333333)",
                          },
                        },
                        "& input": {
                          color: "var(--ig-t-ffffff)",
                        },
                      }}
                    />

                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleVisualizarArquivoSalvo(editandoId, form.nomeArquivoOriginal)}
                      sx={{
                        backgroundColor: "#1998a8",
                        color: "#fff",
                        fontWeight: "bold",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        width: "auto",
                        px: 2,
                        height: 40,
                        "&:hover": {
                          backgroundColor: "#1565c0",
                        },
                      }}
                    >
                      Visualizar
                    </Button>
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  {editandoId && somenteLeitura && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Fechar
                      </Button>

                      {podeCadastrar && templates.find((template) => template.id === editandoId)?.statusAprovacao === "EmElaboracao" && (
                        <Button
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={handleEnviarAprovacao}
                          disabled={enviandoAprovacao}
                          sx={{
                            width: "auto",
                            backgroundColor: "#1998a8",
                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },
                            fontWeight: "bold",
                            py: 1,
                            px: 3,
                          }}
                        >
                          {enviandoAprovacao ? "Enviando..." : "Enviar para Aprovação"}
                        </Button>
                      )}

                      {podeCadastrar && ["EmElaboracao", "Aprovado"].includes(templates.find((template) => template.id === editandoId)?.statusAprovacao) && (
                        <Button
                          variant="contained"
                          startIcon={<FaEdit />}
                          onClick={handleHabilitarEdicao}
                          sx={{
                            width: "auto",
                            backgroundColor: "#1998a8",
                            "&:hover": {
                              backgroundColor: "#1565c0",
                            },
                            fontWeight: "bold",
                            py: 1,
                            px: 3,
                          }}
                        >
                          Editar
                        </Button>
                      )}
                      {podeExcluir && templates.find((template) => template.id === editandoId)?.statusAprovacao === "EmElaboracao" && (
                        <Button
                          variant="contained"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleConfirmarDelete(editandoId)}
                          sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                        >
                          Excluir
                        </Button>
                      )}
                    </>
                  )}
                  {podeExcluir && templates.find((template) => template.id === editandoId)?.statusAprovacao === "Aprovado" && (
                    <Button
                      variant="contained"
                      onClick={() => setConfirmInutilizar(true)}
                      sx={{
                        width: "auto",
                        backgroundColor: "#795548",
                        "&:hover": {
                          backgroundColor: "#5d4037",
                        },
                        fontWeight: "bold",
                        py: 1,
                        px: 3,
                      }}
                    >
                      Inutilizar
                    </Button>
                  )}
                  {(!editandoId || !somenteLeitura) && (
                    <>
                      {editandoId && (
                        <Button
                          variant="outlined"
                          startIcon={<FaTimes />}
                          onClick={handleCancelarForm}
                          sx={{
                            width: "auto",
                            borderColor: "var(--ig-b-555555)",
                            color: "var(--ig-t-cccccc)",
                            "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                            fontWeight: "bold",
                            px: 3,
                          }}
                        >
                          Cancelar
                        </Button>
                      )}
                      {podeCadastrar && (
                        <Button
                          variant="contained"
                          startIcon={<FaSave />}
                          onClick={handleSalvar}
                          sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                        >
                          {editandoId ? "Atualizar" : "Cadastrar"}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {editandoId && abaAtiva === "aprovacao" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <PainelAprovacao
                  titulo="Template de Contrato"
                  statusField={templates.find((template) => template.id === editandoId)?.statusAprovacao || "EmElaboracao"}
                  statusNaoIniciado="EmElaboracao"
                  mostrarCabecalho={false}
                  mostrarBotaoEnviar={false}
                  aprovacao={aprovacaoInfo.aprovacao}
                  autorizacoes={aprovacaoInfo.autorizacoes}
                  historicos={aprovacaoInfo.historicos}
                  idUsuarioLogado={userId}
                  idCriador={templates.find((template) => template.id === editandoId)?.idUsuario ?? null}
                  usuarioEhAprovador={aprovacaoInfo.usuarioEhAprovador}
                  revisarPorAlcadaOuCriador
                  usuarioEmAlcada={aprovacaoInfo.usuarioEmAlcada}
                  onAprovar={handleAprovar}
                  onAbrirReprovar={() => {
                    document.activeElement?.blur();

                    setDialogReprovar({
                      aberto: true,
                      motivo: "",
                    });
                  }}
                  onAbrirQuestionar={() => {
                    document.activeElement?.blur();

                    setDialogQuestionar({
                      aberto: true,
                      motivo: "",
                      idsUsuarioResposta: [],
                      arquivo: null,
                    });
                  }}
                  onAbrirResponder={(pergunta) => {
                    setDialogResponder({
                      aberto: true,
                      resposta: "",
                      pergunta,
                      arquivo: null,
                    });
                  }}
                  onConfirmarRevisar={() => {
                    document.activeElement?.blur();

                    setConfirmRevisar({
                      aberto: true,
                      motivo: "",
                    });
                  }}
                  processando={processandoAprovacao}
                  podeEditar={podeCadastrar}
                  rotaAnexoBase="/api/templates"
                />
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Dialog: Tags disponíveis */}
      <Dialog open={tagsDialogAberto} onClose={() => setTagsDialogAberto(false)} fullWidth maxWidth="lg" slotProps={{ paper: { sx: { height: "90vh" } } }}>
        <DialogTitle sx={{ flex: "0 0 auto" }}>Tags Disponíveis para o Template</DialogTitle>
        <Tabs value={tagsDialogAba} onChange={(e, v) => setTagsDialogAba(v)} sx={{ flex: "0 0 auto", px: 3, borderBottom: "1px solid var(--ig-b-333333)" }}>
          <Tab label="Tags" />
          <Tab label="Condições" />
          <Tab label="Blocos" />
        </Tabs>
        <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <TextField
            size="small"
            fullWidth
            placeholder={
              tagsDialogAba === 0
                ? "Buscar por tag ou descrição..."
                : tagsDialogAba === 1
                  ? "Buscar por condição ou descrição..."
                  : "Buscar por bloco ou descrição..."
            }
            value={tagsBusca}
            onChange={(e) => setTagsBusca(e.target.value)}
            sx={{ mb: 2, flex: "0 0 auto" }}
          />

          {tagsDialogAba === 0 && (
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Table size="small" stickyHeader sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { wordBreak: "break-word" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "16%" }}>Tag</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "32%" }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Exemplo</TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "32%" }}>Uso no texto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tagsFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: "var(--ig-t-888888)" }}>
                        Nenhuma tag encontrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tagsFiltradas.map((item) => {
                      const mostrarCategoria = item.categoria !== categoriaAnterior;
                      categoriaAnterior = item.categoria;
                      return (
                        <React.Fragment key={item.tag}>
                          {mostrarCategoria && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ backgroundColor: "var(--ig-f-252525)", color: "#1998a8", fontWeight: "bold", py: 0.6 }}>
                                {item.categoria}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow hover>
                            <TableCell sx={{ fontFamily: "monospace" }}>[{item.tag}]</TableCell>
                            <TableCell>{item.descricao}</TableCell>
                            <TableCell sx={{ color: "var(--ig-t-666666)" }}>{item.exemplo}</TableCell>
                            <TableCell sx={{ fontStyle: "italic", color: "var(--ig-t-999999)" }}>{item.uso}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tagsDialogAba === 1 && (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: "var(--ig-t-666666)", flex: "0 0 auto" }}>
                Duas formas de usar, com a mesma condição: <code>[SE:Condicao]</code> ... <code>[/SE:Condicao]</code> envolvendo um ou mais{" "}
                <b>parágrafos inteiros</b> (cada marcador sozinho no seu próprio parágrafo) — ou <code>[#Condicao]</code> ... <code>[/Condicao]</code> (sem o
                "SE:") <b>no meio do texto</b>, misturado com outras palavras e tags, quando o trecho condicional for só um pedaço da frase (ex: "o
                locatário[#LocatariaCasado], casado com [LocatariaConjuge],[/LocatariaCasado] declara...").
              </Typography>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { wordBreak: "break-word" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "24%" }}>Condição</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "44%" }}>Descrição</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "32%" }}>Uso no texto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {condicionaisFiltradas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: "var(--ig-t-888888)" }}>
                          Nenhuma condição encontrada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      condicionaisFiltradas.map((item) => {
                        const mostrarCategoria = item.categoria !== categoriaCondicionalAnterior;
                        categoriaCondicionalAnterior = item.categoria;
                        return (
                          <React.Fragment key={item.condicao}>
                            {mostrarCategoria && (
                              <TableRow>
                                <TableCell colSpan={3} sx={{ backgroundColor: "var(--ig-f-252525)", color: "#1998a8", fontWeight: "bold", py: 0.6 }}>
                                  {item.categoria}
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow hover>
                              <TableCell sx={{ fontFamily: "monospace" }}>[SE:{item.condicao}]</TableCell>
                              <TableCell>{item.descricao}</TableCell>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "var(--ig-t-999999)" }}>
                                [SE:{item.condicao}] ... [/SE:{item.condicao}]
                                <br />
                                ou no meio do texto: [#{item.condicao}] ... [/{item.condicao}]
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {tagsDialogAba === 2 && (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: "var(--ig-t-666666)", flex: "0 0 auto" }}>
                Envolva um ponto do template com <code>[BLOCO:Nome]</code> ... <code>[/BLOCO:Nome]</code> (em parágrafo próprio, fora de qualquer outra tabela)
                para que o trecho seja substituído por uma tabela nativa do Word, gerada a partir dos dados da Proposta.
              </Typography>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", "& .MuiTableCell-root": { wordBreak: "break-word" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Bloco</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "48%" }}>Descrição</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "32%" }}>Uso no texto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blocosFiltrados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: "var(--ig-t-888888)" }}>
                          Nenhum bloco encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      blocosFiltrados.map((item) => (
                        <TableRow hover key={item.bloco}>
                          <TableCell sx={{ fontFamily: "monospace" }}>[BLOCO:{item.bloco}]</TableCell>
                          <TableCell>{item.descricao}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "var(--ig-t-999999)", whiteSpace: "pre-line" }}>
                            {item.uso}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagsDialogAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Pré-visualização do template (com tags substituídas ou documento como está) */}
      <Dialog open={previewAberto} onClose={() => setPreviewAberto(false)} fullWidth maxWidth="md">
        <DialogTitle>{previewSemSubstituicao ? "Documento Anexado" : "Pré-visualização do Template"}</DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#fff" }}>
          {previewCarregando ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {!previewSemSubstituicao && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Os valores abaixo são exemplos e não refletem dados reais — servem apenas para conferir o layout do documento.
                </Alert>
              )}
              {!previewSemSubstituicao && condicoesDetectadas.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 1, backgroundColor: "#fafafa" }}>
                  <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600, mb: 0.5 }}>
                    Simular condições (blocos [SE:...] encontrados neste template)
                  </Typography>
                  <FormGroup row>
                    {condicoesDetectadas.map((nome) => {
                      const info = TAGS_CONDICIONAIS_TEMPLATE_CONTRATO.find((c) => c.condicao === nome);
                      return (
                        <FormControlLabel
                          key={nome}
                          control={<Checkbox size="small" checked={!!condicoesAtivas[nome]} onChange={() => handleToggleCondicao(nome)} />}
                          label={info ? `${nome} — ${info.descricao}` : nome}
                          sx={{ color: "#333" }}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              )}
              <Box
                sx={{
                  color: "#000",
                  backgroundColor: "#fff",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                  p: 2,
                  border: "1px solid #ddd",
                  "& table": { borderCollapse: "collapse", width: "100%", marginBottom: "1em" },
                  "& td, & th": { border: "1px solid #ccc", padding: "4px 8px" },
                  "& img": { maxWidth: "100%" },
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Visualização do arquivo salvo em PDF */}
      <Dialog open={visualizacaoPdf.aberto} onClose={fecharVisualizacaoPdf} fullWidth maxWidth="lg">
        <DialogTitle>
          Visualizar Template
          {visualizacaoPdf.nome ? ` — ${visualizacaoPdf.nome}` : ""}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            height: "75vh",
            backgroundColor: "#525659",
          }}
        >
          {visualizacaoPdf.carregando ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : visualizacaoPdf.url ? (
            <iframe
              src={visualizacaoPdf.url}
              title={`Visualização de ${visualizacaoPdf.nome}`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button onClick={fecharVisualizacaoPdf}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmação de exclusão */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>Deseja excluir este template? Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluir}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmação de exclusão do arquivo anexado (só em memória, não persiste sozinho) */}
      <Dialog open={confirmExcluirArquivo} onClose={() => setConfirmExcluirArquivo(false)}>
        <DialogTitle>Excluir arquivo</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            Deseja excluir o arquivo anexado? O arquivo é obrigatório no template — você precisará escolher outro antes de salvar.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmExcluirArquivo(false)}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirArquivo}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      <DialogReprovarAprovacao
        aberto={dialogReprovar.aberto}
        motivo={dialogReprovar.motivo}
        onChangeMotivo={(motivo) =>
          setDialogReprovar((p) => ({
            ...p,
            motivo,
          }))
        }
        onCancelar={() =>
          setDialogReprovar({
            aberto: false,
            motivo: "",
          })
        }
        onConfirmar={handleReprovar}
        processando={processandoAprovacao}
        titulo="Reprovar Template de Contrato"
      />

      <DialogQuestionarAprovacao
        aberto={dialogQuestionar.aberto}
        motivo={dialogQuestionar.motivo}
        onChangeMotivo={(motivo) =>
          setDialogQuestionar((p) => ({
            ...p,
            motivo,
          }))
        }
        idsUsuarioResposta={dialogQuestionar.idsUsuarioResposta}
        onChangeUsuariosResposta={(idsUsuarioResposta) =>
          setDialogQuestionar((p) => ({
            ...p,
            idsUsuarioResposta,
          }))
        }
        arquivo={dialogQuestionar.arquivo}
        onChangeArquivo={(arquivo) =>
          setDialogQuestionar((p) => ({
            ...p,
            arquivo,
          }))
        }
        usuariosAtivos={usuariosAprovadores}
        onCancelar={() =>
          setDialogQuestionar({
            aberto: false,
            motivo: "",
            idsUsuarioResposta: [],
            arquivo: null,
          })
        }
        onConfirmar={handleQuestionar}
        processando={processandoAprovacao}
        titulo="Questionar Template de Contrato"
      />

      <DialogResponderAprovacao
        aberto={dialogResponder.aberto}
        resposta={dialogResponder.resposta}
        pergunta={dialogResponder.pergunta}
        onChangeResposta={(resposta) =>
          setDialogResponder((p) => ({
            ...p,
            resposta,
          }))
        }
        arquivo={dialogResponder.arquivo}
        onChangeArquivo={(arquivo) =>
          setDialogResponder((p) => ({
            ...p,
            arquivo,
          }))
        }
        onCancelar={() =>
          setDialogResponder({
            aberto: false,
            resposta: "",
            pergunta: null,
            arquivo: null,
          })
        }
        onConfirmar={handleResponder}
        processando={processandoAprovacao}
        titulo="Responder Questão — Template de Contrato"
      />

      <DialogReprovarAprovacao
        aberto={confirmRevisar.aberto}
        motivo={confirmRevisar.motivo}
        onChangeMotivo={(motivo) =>
          setConfirmRevisar((p) => ({
            ...p,
            motivo,
          }))
        }
        onCancelar={() =>
          setConfirmRevisar({
            aberto: false,
            motivo: "",
          })
        }
        onConfirmar={handleRevisar}
        processando={processandoAprovacao}
        titulo="Solicitar Revisão"
        contentText='Ao revisar, todas as aprovações do fluxo atual serão invalidadas e o template voltará para "Em Elaboração". Informe o motivo da revisão:'
        rows={4}
        maxLength={255}
        labelConfirmar="Confirmar"
        corConfirmar="#e65100"
      />
      {/* Dialog: reabrir Template aprovado para edição */}
      <Dialog
        open={confirmEditarAprovado}
        onClose={() => {
          if (!processandoAprovacao) {
            setConfirmEditarAprovado(false);
          }
        }}
      >
        <DialogTitle>Editar Template Aprovado</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            Este template já foi aprovado. Ao confirmar, a aprovação atual será invalidada e o template voltará para "Em Elaboração". Após realizar as
            alterações, será necessário enviá-lo novamente para aprovação.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmEditarAprovado(false)} disabled={processandoAprovacao}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirmarEdicaoAprovado}
            disabled={processandoAprovacao}
            sx={{
              backgroundColor: "#1998a8",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {processandoAprovacao ? "Processando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: inutilizar Template aprovado */}
      <Dialog
        open={confirmInutilizar}
        onClose={() => {
          if (!processandoAprovacao) {
            setConfirmInutilizar(false);
          }
        }}
      >
        <DialogTitle>Inutilizar Template</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            Este template está aprovado e não pode ser excluído. Ao inutilizá-lo, ele deixará de estar disponível para novas utilizações. Deseja continuar?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmInutilizar(false)} disabled={processandoAprovacao}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleInutilizar}
            disabled={processandoAprovacao}
            sx={{
              backgroundColor: "#795548",
              "&:hover": {
                backgroundColor: "#5d4037",
              },
            }}
          >
            {processandoAprovacao ? "Processando..." : "Inutilizar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
